import React from "react";
import { ArrowRight, Handshake, Home, RefreshCw, LandPlot, Scale } from "lucide-react";
import ProponiWizard from "@/components/ProponiWizard";
import SEO from "@/components/SEO";

const MODELS = [
  {
    id: "partnership",
    icon: Handshake,
    title: "Partnership di riqualificazione",
    tagline: "Non vendere sotto prezzo. Ristrutturiamo noi, dividiamo il ricavato.",
    how: [
      "Analizziamo il tuo immobile e stimiamo il valore finale post-ristrutturazione.",
      "Firmiamo un contratto chiaro con il ricavato di rivendita già ripartito.",
      "Ristrutturiamo a nostre spese e curiamo la vendita.",
      "Al closing, ricevi la tua quota concordata.",
    ],
    example: {
      title: "Esempio numerico",
      rows: [
        ["Valore attuale immobile", "€ 250.000"],
        ["Costo ristrutturazione (Ri.Sco.)", "€ 120.000"],
        ["Valore vendita post lavori", "€ 520.000"],
        ["Utile lordo condiviso", "€ 150.000"],
        ["La tua quota (esempio 60%)", "€ 90.000 extra"],
      ],
    },
    fit: "Per chi ha un immobile maltenuto e non vuole svenderlo o gestire lavori in proprio.",
  },
  {
    id: "vendita",
    icon: Home,
    title: "Vendita diretta",
    tagline: "Ti proponiamo un'offerta concreta, senza intermediari.",
    how: [
      "Sopralluogo tecnico e valutazione di mercato.",
      "Offerta scritta entro 15 giorni.",
      "Rogito rapido con notaio di fiducia.",
      "Compriamo noi, gestiamo noi tutto il resto.",
    ],
    example: {
      title: "A chi conviene",
      rows: [
        ["Immobile da ristrutturare in zone di interesse", "→ offerta rapida"],
        ["Palazzi interi obsoleti", "→ valutazione dedicata"],
        ["Edifici da demolire", "→ analisi di ricostruzione"],
      ],
    },
    fit: "Per chi vuole liquidare velocemente senza gestire vendita e trattative.",
  },
  {
    id: "permuta",
    icon: RefreshCw,
    title: "Permuta immobiliare",
    tagline: "Cedi il vecchio, ricevi il nuovo (o il ristrutturato).",
    how: [
      "Valutazione dell'immobile attuale.",
      "Definizione delle unità che riceverai nel nuovo progetto.",
      "Firma del contratto preliminare di permuta con conguaglio se dovuto.",
      "Consegna delle nuove unità al completamento dei lavori.",
    ],
    example: {
      title: "Esempio",
      rows: [
        ["Cedi", "Immobile / terreno del valore € 300.000"],
        ["Ricevi", "2 unità nuove nel progetto Ri.Sco."],
        ["Valore complessivo unità", "€ 340.000"],
        ["Conguaglio", "€ 40.000 (o gestibile in altre modalità)"],
      ],
    },
    fit: "Per chi vuole trasformare un asset vecchio in unità nuove senza esborso monetario.",
  },
  {
    id: "edificazione",
    icon: LandPlot,
    title: "Edificazione ex novo in partnership",
    tagline: "Hai il terreno? Costruiamo insieme e condividiamo l'operazione.",
    how: [
      "Analisi di fattibilità e possibilità edificatorie.",
      "Progetto architettonico e business plan condiviso.",
      "Ri.Sco. finanzia e realizza la costruzione.",
      "Ripartizione degli utili o delle unità secondo accordo.",
    ],
    example: {
      title: "Esempio numerico",
      rows: [
        ["Valore terreno (tuo apporto)", "€ 200.000"],
        ["Costo costruzione (Ri.Sco.)", "€ 900.000"],
        ["Valore finale (6 unità)", "€ 1.700.000"],
        ["Utile lordo", "€ 600.000"],
        ["Tua quota (esempio)", "€ 240.000 senza esborsi"],
      ],
    },
    fit: "Per proprietari di terreni edificabili che vogliono valorizzarli senza indebitarsi.",
  },
];

const HERO =
  "https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxyZWFsJTIwZXN0YXRlJTIwaW52ZXN0b3IlMjBtZWV0aW5nfGVufDB8fHx8MTc4MzQyOTQxOXww&ixlib=rb-4.1.0&q=85";

export default function Operazioni() {
  return (
    <div>
      <SEO
        title="Proponi il tuo affare immobiliare — Partnership, Permuta, Vendita | Ri.Sco. Napoli"
        description="Hai un palazzo, un appartamento o un terreno a Napoli e provincia? Proponi la tua operazione: partnership di riqualificazione, permuta, vendita diretta, edificazione condivisa. Contratti chiari con i nostri legali."
        path="/operazioni-immobiliari"
      />
      <section className="relative min-h-screen flex items-end overflow-hidden -mt-20 pt-20 grain" data-testid="op-hero">
        <div className="absolute inset-0">
          <img src={HERO} alt="Operazioni immobiliari" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-risco-ink/95 via-risco-ink/60 to-risco-ink/30" />
        </div>
        <div className="relative container-x pb-20 md:pb-28 w-full">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="rule-tag text-stone-300 text-xs uppercase tracking-[0.28em] mb-8">Operazioni Immobiliari · 03</div>
              <h1 className="text-risco-bg leading-[0.95]">
                <span className="font-heading font-medium text-4xl sm:text-5xl md:text-6xl lg:text-8xl block tracking-tighter">Chiunque può diventare</span>
                <span className="font-display italic text-4xl sm:text-5xl md:text-6xl lg:text-8xl block">un imprenditore immobiliare.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <p className="text-stone-200 text-base leading-relaxed max-w-md">
                Hai un palazzo, un terreno, un immobile da valorizzare? Non serve capitale e non serve esperienza.
                Ti proponiamo 4 modelli di collaborazione, tutti con contratti chiari.
              </p>
              <a href="#proponi" className="mt-8 inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-risco-accent hover:bg-risco-accent-dark text-white font-medium transition-colors" data-testid="op-hero-cta">
                Proponi il tuo affare <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="op-models">
        <div className="container-x">
          <div className="overline mb-3">4 modelli di collaborazione</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-risco-ink tracking-tight max-w-3xl">Scegli come lavorare con noi</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {MODELS.map((m) => (
              <div key={m.id} className="risco-card p-8 flex flex-col" data-testid={`op-model-${m.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-md border border-risco-accent/50 bg-risco-accent-soft flex items-center justify-center shrink-0">
                    <m.icon className="w-6 h-6 text-risco-accent" />
                  </div>
                  <div>
                    <div className="font-heading text-xl text-risco-ink">{m.title}</div>
                    <div className="text-risco-accent text-sm mt-1">{m.tagline}</div>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="overline mb-3">Come funziona</div>
                  <ol className="space-y-2 text-sm text-stone-700">
                    {m.how.map((s, i) => (
                      <li key={s} className="flex gap-3"><span className="text-risco-accent font-bold">{i + 1}.</span>{s}</li>
                    ))}
                  </ol>
                </div>
                <div className="mt-6 p-4 rounded-md bg-white border border-risco-border">
                  <div className="overline mb-2">{m.example.title}</div>
                  <div className="space-y-1.5 text-sm">
                    {m.example.rows.map((r) => (
                      <div key={r[0]} className="flex justify-between gap-4 border-b border-risco-border pb-1.5 last:border-0">
                        <span className="text-risco-muted">{r[0]}</span>
                        <span className="text-risco-ink font-medium whitespace-nowrap">{r[1]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-5 text-sm text-risco-muted"><strong className="text-risco-ink">A chi è adatto:</strong> {m.fit}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-y border-risco-border bg-risco-alt" data-testid="op-legal">
        <div className="container-x flex flex-col md:flex-row items-start gap-6">
          <div className="w-14 h-14 rounded-md bg-risco-accent-soft border border-risco-accent/50 flex items-center justify-center shrink-0">
            <Scale className="w-7 h-7 text-risco-accent" />
          </div>
          <div>
            <h3 className="font-heading text-2xl text-risco-ink">Contratti chiari e trasparenti</h3>
            <p className="text-risco-muted mt-3 max-w-3xl">Ogni operazione si formalizza con contratti chiari: condizioni definite, ripartizioni scritte, tempi vincolanti. Nessuna zona grigia. Nessuna sorpresa.</p>
          </div>
        </div>
      </section>

      <section id="proponi" className="py-20 md:py-28" data-testid="op-proponi-section">
        <div className="container-x max-w-4xl">
          <div className="text-center mb-10">
            <div className="overline mb-3">Proponi il tuo affare</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-risco-ink tracking-tighter">Bastano 5 step. Ti rispondiamo in 48 ore.</h2>
            <p className="text-risco-muted mt-4 max-w-2xl mx-auto">Descrivici la tua proposta: la analizza il nostro team di architetti, tecnici e valutatori.</p>
          </div>
          <ProponiWizard />
        </div>
      </section>
    </div>
  );
}
